import { normalizeScalePlu, trimScalePluLeadingZeros } from '@shared/barcode';
import { sellByWeightFromUnit } from '@shared/quantity';
import type { CatalogProductRow } from '../domain/catalogTypes';
import type { Product } from '../domain/productTypes';
import type { ProductVariant } from '../domain/productVariants';
import { normalizeProductCode } from '../domain/catalogValidation';
import { BRANCH_STOCK_JOIN_SQL, mapRemoteSnapshotToSearchRow } from './catalogReadRepo';
import { hasNetworkConnection, lookupBarcodeRemotely } from './barcodeLookup';
import type { BarcodeLookupSuggestion, ProductLookupResult } from '../domain/barcodeLookup';
import { remoteLookupSoftMiss } from '../domain/barcodeScanField';
import { getSessionOrganizationId } from '../../shell/session/sessionOrganization';
import { useAuthStore } from '../../auth/store/useAuthStore';
import {
  aggregateRemoteStocksByProduct,
  loadWebCatalogSnapshotRows,
} from './productCatalogRemoteSnapshot';
import { getCatalogRamSnapshotCache } from './catalogRamSnapshotCache';

export { barcodeSearchFieldValue, remoteLookupSoftMiss } from '../domain/barcodeScanField';

const pluLookupKeys = (plu: string): string[] => {
  const padded = normalizeScalePlu(plu);
  const trimmed = trimScalePluLeadingZeros(plu);
  return padded === trimmed ? [padded] : [padded, trimmed];
};

const matchesPluCode = (code: string, pluKeys: string[]): boolean => {
  const normalized = normalizeProductCode(code);
  if (!normalized) return false;
  return pluKeys.some((key) => normalized === key);
};

const mapDbProductLookupRow = (row: {
  id: string;
  name: string;
  price: number;
  sku: string | null;
  barcode: string | null;
  category_id: string | null;
  is_active: number;
  created_at: string | null;
  updated_at: string | null;
  organization_id: string | null;
  item_type: string | null;
  is_misc: number | null;
  unit_of_measure: string | null;
  stockId: string | null;
  stockQuantity: number | null;
  stockMinQuantity: number | null;
}): CatalogProductRow => {
  const unitOfMeasure =
    row.unit_of_measure != null && String(row.unit_of_measure).trim() !== ''
      ? (String(row.unit_of_measure).trim() as Product['unitOfMeasure'])
      : 'unit';

  return {
    id: row.id,
    name: row.name,
    price: row.price,
    sku: row.sku ?? '',
    barcode: row.barcode ?? null,
    categoryId:
      row.category_id != null && String(row.category_id).trim() !== ''
        ? String(row.category_id).trim()
        : null,
    itemType: row.item_type === 'service' ? 'service' : 'physical',
    isMisc: row.is_misc === 1,
    organizationId: row.organization_id ?? null,
    isActive: row.is_active === 1,
    createdAt: row.created_at,
    updatedAt: row.updated_at,
    unitOfMeasure,
    sellByWeight: sellByWeightFromUnit(unitOfMeasure),
    stockQuantity: row.stockQuantity != null ? Number(row.stockQuantity) : null,
    stockMinQuantity: row.stockMinQuantity != null ? Number(row.stockMinQuantity) : null,
  };
};

const findProductByPluFromWebSnapshot = async ({
  plu,
  includeInactive = false,
  branchId = null,
}: {
  plu: string;
  includeInactive?: boolean;
  branchId?: string | null;
}): Promise<CatalogProductRow | null> => {
  const pluKeys = pluLookupKeys(plu);
  if (pluKeys.length === 0) return null;

  const organizationId = await getSessionOrganizationId();
  if (!organizationId) return null;

  const { session } = useAuthStore.getState();
  if (!session) return null;

  const snapshot = await loadWebCatalogSnapshotRows();
  if (!snapshot) return null;

  const { products, stocks } = snapshot;
  const stockByProduct = branchId ? aggregateRemoteStocksByProduct(branchId, stocks) : new Map();

  const product = products.find((p) => {
    if (!p?.id || String(p.organization_id ?? '').trim() !== organizationId) {
      return false;
    }
    const sku = String(p.sku ?? '');
    const barcode = p.barcode ? String(p.barcode) : '';
    return matchesPluCode(sku, pluKeys) || (barcode ? matchesPluCode(barcode, pluKeys) : false);
  });

  if (!product) return null;
  if (!includeInactive && product.is_active === false) return null;

  const row = mapRemoteSnapshotToSearchRow(product, branchId ? stockByProduct.get(product.id) : undefined);
  return {
    ...row,
    isActive: row.isActive !== false,
    createdAt: row.createdAt ?? null,
    updatedAt: row.updatedAt ?? null,
  } satisfies CatalogProductRow;
};

export const findProductByPlu = async ({
  plu,
  includeInactive = false,
  branchId = null,
}: {
  plu: string;
  includeInactive?: boolean;
  branchId?: string | null;
}): Promise<CatalogProductRow | null> => {
  const pluKeys = pluLookupKeys(plu);
  if (pluKeys.length === 0) return null;

  return findProductByPluFromWebSnapshot({ plu, includeInactive, branchId });
};

const findProductByCodeFromWebSnapshot = async ({
  code,
  includeInactive = false,
  branchId = null,
}: {
  code: string;
  includeInactive?: boolean;
  branchId?: string | null;
}): Promise<CatalogProductRow | null> => {
  const normalizedCode = normalizeProductCode(code);
  if (!normalizedCode) {
    return null;
  }

  const organizationId = await getSessionOrganizationId();
  if (!organizationId) return null;

  const { session } = useAuthStore.getState();
  if (!session) return null;

  const snapshot = await loadWebCatalogSnapshotRows();
  if (!snapshot) return null;

  const { products, stocks } = snapshot;
  const stockByProduct = branchId ? aggregateRemoteStocksByProduct(branchId, stocks) : new Map();

  const product = products.find((p) => {
    if (!p?.id || String(p.organization_id ?? '').trim() !== organizationId) {
      return false;
    }
    const sku = normalizeProductCode(String(p.sku ?? ''));
    const barcode = p.barcode ? normalizeProductCode(String(p.barcode)) : '';
    return sku === normalizedCode || barcode === normalizedCode;
  });

  if (!product) {
    return null;
  }
  if (!includeInactive && product.is_active === false) {
    return null;
  }

  const row = mapRemoteSnapshotToSearchRow(product, branchId ? stockByProduct.get(product.id) : undefined);
  return {
    ...row,
    isActive: row.isActive !== false,
    createdAt: row.createdAt ?? null,
    updatedAt: row.updatedAt ?? null,
  } satisfies CatalogProductRow;
};

export const findProductByCode = async ({
  code,
  includeInactive = false,
  branchId = null,
}: {
  code: string;
  includeInactive?: boolean;
  branchId?: string | null;
}): Promise<CatalogProductRow | null> => {
  const normalizedCode = normalizeProductCode(code);
  if (!normalizedCode) {
    return null;
  }

  return findProductByCodeFromWebSnapshot({ code, includeInactive, branchId });
};

const mapVariantLookupRow = (row: {
  variant_id: string;
  variant_org_id: string;
  variant_product_id: string;
  variant_sku: string | null;
  variant_barcode: string | null;
  variant_name_suffix: string | null;
  variant_attributes_json: string | null;
  variant_price: number | null;
  variant_is_active: number;
  id: string;
  name: string;
  price: number;
  sku: string | null;
  barcode: string | null;
  category_id: string | null;
  is_active: number;
  created_at: string | null;
  updated_at: string | null;
  organization_id: string | null;
  item_type: string | null;
  is_misc: number | null;
  stockId: string | null;
  stockQuantity: number | null;
  stockMinQuantity: number | null;
}): { product: CatalogProductRow; variant: ProductVariant } => {
  let attributes: Record<string, string> = {};
  if (row.variant_attributes_json) {
    try {
      const parsed = JSON.parse(row.variant_attributes_json) as Record<string, unknown>;
      for (const [key, value] of Object.entries(parsed)) {
        if (typeof value === 'string') attributes[key] = value;
        else if (value != null) attributes[key] = String(value);
      }
    } catch {
      attributes = {};
    }
  }

  const product: CatalogProductRow = {
    id: row.id,
    name: row.name,
    price: row.price,
    sku: row.sku ?? '',
    barcode: row.barcode ?? null,
    categoryId:
      row.category_id != null && String(row.category_id).trim() !== ''
        ? String(row.category_id).trim()
        : null,
    itemType: row.item_type === 'service' ? 'service' : 'physical',
    isMisc: row.is_misc === 1,
    organizationId: row.organization_id ?? null,
    isActive: row.is_active === 1,
    createdAt: row.created_at,
    updatedAt: row.updated_at,
    stockQuantity: row.stockQuantity != null ? Number(row.stockQuantity) : null,
    stockMinQuantity: row.stockMinQuantity != null ? Number(row.stockMinQuantity) : null,
  };

  const variant: ProductVariant = {
    id: row.variant_id,
    organizationId: row.variant_org_id,
    productId: row.variant_product_id,
    sku: row.variant_sku,
    barcode: row.variant_barcode,
    nameSuffix: row.variant_name_suffix,
    attributes,
    price: row.variant_price,
    isActive: row.variant_is_active === 1,
  };

  return { product, variant };
};

export const findVariantByCode = async ({
  code,
  includeInactive = false,
  branchId = null,
}: {
  code: string;
  includeInactive?: boolean;
  branchId?: string | null;
}): Promise<{ product: CatalogProductRow; variant: ProductVariant } | null> => {
  // ADR-017 Fase 4: SQLite local eliminado — leer de catalogRamSnapshotCache.
  const normalizedCode = normalizeProductCode(code);
  if (!normalizedCode) return null;

  const organizationId = await getSessionOrganizationId();
  if (!organizationId) return null;

  const cached = getCatalogRamSnapshotCache();
  if (!cached) return null;

  const variantRow = cached.productVariants.find((v) => {
    if (!v?.id) return false;
    const vOrgId = String((v as Record<string, unknown>).organization_id ?? '');
    if (vOrgId && vOrgId !== organizationId) return false;
    if ((v as Record<string, unknown>).is_active === false) return false;
    const vSku = normalizeProductCode(String((v as Record<string, unknown>).sku ?? ''));
    const vBarcodeRaw = (v as Record<string, unknown>).barcode;
    const vBarcode = vBarcodeRaw != null ? normalizeProductCode(String(vBarcodeRaw)) : '';
    return vSku === normalizedCode || vBarcode === normalizedCode;
  });
  if (!variantRow) return null;

  const v = variantRow as Record<string, unknown>;
  const productId = String(v.product_id ?? '');
  const product = cached.products.find((p) => String(p.id) === productId);
  if (!product) return null;
  if (String(product.organization_id ?? '') !== organizationId) return null;
  if (!includeInactive && product.is_active === false) return null;

  let stockId: string | null = null;
  let stockQuantity: number | null = null;
  let stockMinQuantity: number | null = null;
  if (branchId) {
    const stock = cached.stocks.find(
      (s) =>
        String(s.product_id) === productId &&
        s.branch_id === branchId &&
        String(s.variant_id ?? '') === String(variantRow.id),
    );
    if (stock) {
      stockId = stock.id ?? null;
      stockQuantity = Number(stock.quantity ?? 0);
      stockMinQuantity = Number(stock.min_quantity ?? 0);
    }
  }

  return mapVariantLookupRow({
    variant_id: String(variantRow.id),
    variant_org_id: String(v.organization_id ?? organizationId),
    variant_product_id: productId,
    variant_sku: v.sku != null ? String(v.sku) : null,
    variant_barcode: v.barcode != null ? String(v.barcode) : null,
    variant_name_suffix: v.name_suffix != null ? String(v.name_suffix) : null,
    variant_attributes_json:
      v.attributes_json != null
        ? typeof v.attributes_json === 'string'
          ? v.attributes_json
          : JSON.stringify(v.attributes_json)
        : null,
    variant_price: v.price != null ? Number(v.price) : null,
    variant_is_active: v.is_active !== false ? 1 : 0,
    id: String(product.id),
    name: String(product.name ?? ''),
    price: Number(product.price ?? 0),
    sku: product.sku ?? '',
    barcode: product.barcode ?? null,
    category_id: product.category_id ?? null,
    is_active: product.is_active !== false ? 1 : 0,
    created_at: product.created_at ?? null,
    updated_at: product.updated_at ?? null,
    organization_id: product.organization_id != null ? String(product.organization_id) : null,
    item_type: product.item_type ?? null,
    is_misc: product.is_misc ? 1 : 0,
    stockId,
    stockQuantity,
    stockMinQuantity,
  });
};

export const buildProductDraftFromSuggestion = (params: {
  barcode: string;
  suggestion: BarcodeLookupSuggestion;
}) => {
  const code = normalizeProductCode(params.barcode);
  // ponytail: no copiar categoryHint de OFF (drinks/others no son ids nuestros).
  return {
    name: params.suggestion.name,
    sku: code,
    barcode: code,
  };
};

export const lookupProductByBarcode = async ({
  code,
  includeInactive = false,
  allowRemoteLookup = true,
  branchId = null,
}: {
  code: string;
  includeInactive?: boolean;
  allowRemoteLookup?: boolean;
  branchId?: string | null;
}): Promise<ProductLookupResult> => {
  const barcode = normalizeProductCode(code);
  const localProduct = await findProductByCode({ code: barcode, includeInactive, branchId });

  if (localProduct) {
    return {
      kind: 'local_hit',
      source: 'local',
      barcode,
      product: localProduct,
    };
  }

  const localVariant = await findVariantByCode({ code: barcode, includeInactive, branchId });
  if (localVariant) {
    return {
      kind: 'local_variant_hit',
      source: 'local',
      barcode,
      product: localVariant.product,
      variant: localVariant.variant,
    };
  }

  if (!allowRemoteLookup) {
    return {
      kind: 'not_found',
      source: null,
      barcode,
    };
  }

  const online = await hasNetworkConnection();
  if (!online) {
    return {
      kind: 'offline_miss',
      source: null,
      barcode,
    };
  }

  // Fallos de API/sesión/red → miss soft (no toast "No se pudo escanear").
  let remoteResult;
  try {
    remoteResult = await lookupBarcodeRemotely(barcode);
  } catch {
    return remoteLookupSoftMiss(barcode);
  }

  if (!remoteResult.found || !remoteResult.suggestion) {
    return {
      kind: 'not_found',
      source: remoteResult.source === 'local' ? null : remoteResult.source,
      barcode,
    };
  }

  return {
    kind: 'remote_hit',
    source: remoteResult.source === 'local' ? 'supabase_cache' : remoteResult.source,
    barcode,
    confidence: remoteResult.confidence,
    requiresReview: remoteResult.requiresReview,
    suggestion: remoteResult.suggestion,
  };
};
